import { BusinessScenario, BusinessCategory } from '@/types/scenarios/business';
import { businessCategories } from './categories';

// Import scenario files as they're created
import { smallRetail } from './scenarios/small-retail';
import { saasStartup } from './scenarios/saas-startup';
// import { enterpriseSales } from './scenarios/enterprise-sales';
// import { professionalServices } from './scenarios/professional-services';

// Central registry of all business scenarios
const businessScenarioRegistry = new Map<string, BusinessScenario>();

// Register all business scenarios
[
  smallRetail,
  saasStartup,
  // enterpriseSales,
  // professionalServices
].forEach(scenario => {
  businessScenarioRegistry.set(scenario.meta.id, scenario);
});

// Utility functions for accessing business scenarios
export const getBusinessScenarioById = (id: string): BusinessScenario | undefined => {
  return businessScenarioRegistry.get(id);
};

export const getBusinessScenarioBySlug = (slug: string): BusinessScenario | undefined => {
  return Array.from(businessScenarioRegistry.values()).find(s => s.meta.slug === slug);
};

export const getBusinessScenariosBySize = (size: 'startup' | 'small' | 'medium' | 'enterprise'): BusinessScenario[] => {
  return Array.from(businessScenarioRegistry.values()).filter(s => s.meta.size === size);
};

export const getAllBusinessScenarios = (): BusinessScenario[] => {
  return Array.from(businessScenarioRegistry.values());
};

export const getFeaturedBusinessScenarios = (): BusinessScenario[] => {
  return Array.from(businessScenarioRegistry.values()).filter(s => s.meta.isFeatured);
};

export const getRelatedBusinessScenarios = (scenarioId: string, limit = 3): BusinessScenario[] => {
  const scenario = getBusinessScenarioById(scenarioId);
  if (!scenario) return [];

  const related: BusinessScenario[] = [];
  const allScenarios = getAllBusinessScenarios();
  
  // First, get scenarios of same size
  const sameSizeScenarios = getBusinessScenariosBySize(scenario.meta.size)
    .filter(s => s.meta.id !== scenarioId);
  
  // Shuffle and add up to limit
  const shuffled = sameSizeScenarios.sort(() => Math.random() - 0.5);
  related.push(...shuffled.slice(0, limit));
  
  // If still need more, get any other scenarios
  if (related.length < limit) {
    const otherScenarios = allScenarios
      .filter(s => 
        s.meta.id !== scenarioId && 
        !related.some(r => r.meta.id === s.meta.id)
      )
      .sort(() => Math.random() - 0.5);
    
    related.push(...otherScenarios.slice(0, limit - related.length));
  }
  
  return related.slice(0, limit);
};

export const searchBusinessScenarios = (query: string): BusinessScenario[] => {
  const lowerQuery = query.toLowerCase();
  return Array.from(businessScenarioRegistry.values()).filter(scenario => {
    const meta = scenario.meta;
    return (
      meta.name.toLowerCase().includes(lowerQuery) ||
      meta.tagline.toLowerCase().includes(lowerQuery) ||
      meta.description.toLowerCase().includes(lowerQuery) ||
      meta.challenges.some(c => c.toLowerCase().includes(lowerQuery)) ||
      meta.goals.some(g => g.toLowerCase().includes(lowerQuery))
    );
  });
};

export const getBusinessCategories = (): BusinessCategory[] => {
  return businessCategories;
};

export const getBusinessCategoryById = (id: string): BusinessCategory | undefined => {
  return businessCategories.find(cat => cat.id === id);
};

export const getBusinessCategoryBySlug = (slug: string): BusinessCategory | undefined => {
  return businessCategories.find(cat => cat.slug === slug);
};

// Export the registry for advanced use cases
export { businessScenarioRegistry };